import React, { useEffect, useState } from 'react';
import portalApi from '../../../../lib/portalApi';
import {
  RefreshCw,
  Plus,
  Edit3,
  X,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
} from 'lucide-react';

export default function StartupInvestorReadinessPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Modals state
  const [editTermsOpen, setEditTermsOpen] = useState(false);
  const [termsForm, setTermsForm] = useState({
    targetAmount: '₹1.5 Cr',
    timeline: 'Q1 2027',
    minimumCheque: '₹10L',
  });
  const [termsSaving, setTermsSaving] = useState(false);

  const [logModalOpen, setLogModalOpen] = useState(false);
  const [interactionForm, setInteractionForm] = useState({
    investorName: '',
    firmType: 'VC fund',
    notes: '',
    nextAction: '',
    status: 'Follow up',
  });
  const [interactionSaving, setInteractionSaving] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await portalApi.getStartupPortalInvestorReadiness();
      setData(res);
      if (res?.fundraisingNeeds) {
        setTermsForm({
          targetAmount: res.fundraisingNeeds.targetRound || '₹1.5 Cr',
          timeline: res.fundraisingNeeds.timeline || 'Q1 2027',
          minimumCheque: res.fundraisingNeeds.minimumCheque || '₹10L',
        });
      }
    } catch (err) {
      console.error('Failed to load investor readiness:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSaveTerms = async (e) => {
    e.preventDefault();
    try {
      setTermsSaving(true);
      await portalApi.updateStartupPortalFundingNeeds(termsForm);
      setEditTermsOpen(false);
      await loadData();
    } catch (err) {
      alert(err.message || 'Failed to update fundraising terms.');
    } finally {
      setTermsSaving(false);
    }
  };

  const handleSaveInteraction = async (e) => {
    e.preventDefault();
    if (!interactionForm.investorName.trim()) {
      alert('Investor name is required.');
      return;
    }
    try {
      setInteractionSaving(true);
      const formattedName = `${interactionForm.investorName} (${interactionForm.firmType})`;
      await portalApi.addStartupPortalInvestorInteraction({
        investorName: formattedName,
        stage: interactionForm.status === 'Done' ? 'Term Sheet' : 'Intro Meeting',
        notes: interactionForm.notes,
        nextAction: interactionForm.nextAction,
      });
      setLogModalOpen(false);
      setInteractionForm({
        investorName: '',
        firmType: 'VC fund',
        notes: '',
        nextAction: '',
        status: 'Follow up',
      });
      await loadData();
    } catch (err) {
      alert(err.message || 'Failed to record interaction.');
    } finally {
      setInteractionSaving(false);
    }
  };

  const readinessScore = data?.readinessScore || 65;

  const readinessBars = data?.readinessBars || [
    { name: 'Pitch deck', status: 'Done', pct: 100, color: 'emerald' },
    { name: 'One-pager', status: 'Done', pct: 100, color: 'emerald' },
    { name: 'Financial model', status: '60%', pct: 60, color: 'amber' },
    { name: 'Data room', status: '20%', pct: 20, color: 'rose' },
  ];

  const materials = data?.materials || [
    {
      id: 'mat_1',
      title: 'Pitch deck v3',
      updated: 'updated 26 Oct',
      status: 'Approved',
    },
    {
      id: 'mat_2',
      title: 'One-pager v2',
      updated: 'updated 20 Oct',
      status: 'Approved',
    },
    {
      id: 'mat_3',
      title: 'Financial model v1',
      updated: 'updated 12 Oct',
      status: 'In review',
    },
  ];

  const fundraisingNeeds = data?.fundraisingNeeds || {
    targetRound: '₹1.5 Cr',
    timeline: 'Q1 2027',
    minimumCheque: '₹10L',
  };

  const followUps = data?.followUps || [
    {
      id: 'fu_1',
      title: 'Send financials',
      investor: 'Investor A · Mon',
      status: 'Due',
    },
    {
      id: 'fu_2',
      title: 'Intro call',
      investor: 'Investor B · Fri',
      status: 'Booked',
    },
    {
      id: 'fu_3',
      title: 'Thank-you note',
      investor: 'Investor C',
      status: 'Open',
    },
  ];

  const interactions = data?.interactions || [
    {
      id: 'int_1',
      investorName: 'Investor A (angel network)',
      details: 'Intro call 24 Oct · Next: send financials',
      status: 'Follow up',
    },
    {
      id: 'int_2',
      investorName: 'Investor B (VC fund)',
      details: 'Email intro 21 Oct · Awaiting reply',
      status: 'Waiting',
    },
    {
      id: 'int_3',
      investorName: 'Investor C (family office)',
      details: 'Met 15 Oct · Asked for deck',
      status: 'Done',
    },
  ];

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-[#F2F2F4] tracking-tight">
            Investor Readiness
          </h1>
          <p className="text-xs text-[#8E8E99] mt-0.5">
            Diligence readiness score, fundraising parameters, approved materials, and investor pipeline.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={loadData}
            className="p-2 rounded-xl border border-[#2C2C34] bg-[#131318] text-[#8E8E99] hover:text-white transition-colors"
            title="Refresh data"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* ========================================================
          [1] TOP BANNER: Restricted Access Notice
         ======================================================== */}
      <div className="bg-[#131318] border border-[#2C2C34] rounded-2xl px-5 py-3 text-xs text-[#8E8E99] font-medium flex items-center justify-between">
        <span>
          Visible to founders and Admin. Mentor access: To Confirm. Team members need &quot;Investor access&quot;.
        </span>
      </div>

      {/* ========================================================
          ROW 2: [2] Readiness · 65% (Left) + [3] Materials (Right)
         ======================================================== */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
        {/* [2] READINESS · 65% */}
        <div className="bg-[#131318] border border-[#2C2C34] rounded-2xl p-5 sm:p-6 space-y-5 flex flex-col justify-between">
          <div className="flex items-center justify-between pb-2 border-b border-[#2C2C34]/60">
            <h2 className="text-sm font-bold text-[#F2F2F4]">
              Readiness · {readinessScore}%
            </h2>
          </div>

          <div className="space-y-4">
            {readinessBars.map((bar, idx) => {
              const isDone = bar.status === 'Done';
              const isAmber = bar.color === 'amber' || bar.pct === 60;
              const isRose = bar.color === 'rose' || bar.pct === 20;

              return (
                <div key={idx} className="space-y-1.5">
                  <div className="flex items-center justify-between text-xs font-semibold">
                    <span className="text-[#8E8E99]">{bar.name}</span>
                    <span
                      className={
                        isDone
                          ? 'text-emerald-400'
                          : isAmber
                          ? 'text-[#F2F2F4]'
                          : 'text-[#F2F2F4]'
                      }
                    >
                      {bar.status}
                    </span>
                  </div>

                  {/* Horizontal Bar */}
                  <div className="h-3 w-full rounded-full bg-[#0E0E12] border border-[#2C2C34] overflow-hidden p-0.5">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${
                        isDone
                          ? 'bg-emerald-500'
                          : isAmber
                          ? 'bg-amber-500'
                          : isRose
                          ? 'bg-rose-500'
                          : 'bg-emerald-500'
                      }`}
                      style={{ width: `${bar.pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="text-[11px] text-[#6E6E78] pt-2 border-t border-[#2C2C34]/60">
            Institutional investor diligence standards verified by accelerator.
          </div>
        </div>

        {/* [3] MATERIALS */}
        <div className="bg-[#131318] border border-[#2C2C34] rounded-2xl p-5 sm:p-6 space-y-4 flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between pb-2 border-b border-[#2C2C34]/60">
              <h2 className="text-sm font-bold text-[#F2F2F4]">Materials</h2>
              <span className="text-[11px] text-[#8E8E99]">Vault verified</span>
            </div>

            <div className="space-y-3 pt-3">
              {materials.map((mat) => {
                const isApproved = mat.status === 'Approved';

                return (
                  <div
                    key={mat.id}
                    className="p-3.5 rounded-xl bg-[#08080A] border border-[#2C2C34] flex items-center justify-between gap-3 hover:border-[#3E3E48] transition-colors"
                  >
                    <div>
                      <div className="text-xs font-bold text-[#F2F2F4]">
                        {mat.title}
                      </div>
                      <div className="text-[11px] text-[#8E8E99] mt-0.5">
                        {mat.updated}
                      </div>
                    </div>

                    {/* Status Pill Badge */}
                    <div>
                      {isApproved ? (
                        <span className="px-3.5 py-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-400 text-xs font-semibold">
                          Approved
                        </span>
                      ) : (
                        <span className="px-3.5 py-1 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-400 text-xs font-semibold">
                          In review
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="text-[11px] text-[#6E6E78] pt-2 border-t border-[#2C2C34]/60">
            Approved files automatically link to the investor deal room.
          </div>
        </div>
      </div>

      {/* ========================================================
          ROW 3: [4] Fundraising needs (Left) + [5] Follow-ups (Right)
         ======================================================== */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
        {/* [4] FUNDRAISING NEEDS */}
        <div className="bg-[#131318] border border-[#2C2C34] rounded-2xl p-5 sm:p-6 space-y-4 flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between pb-2 border-b border-[#2C2C34]/60">
              <h2 className="text-sm font-bold text-[#F2F2F4]">Fundraising needs</h2>
              <button
                onClick={() => setEditTermsOpen(true)}
                className="text-xs text-[#FF8EC5] hover:text-white font-medium flex items-center gap-1 transition-colors"
              >
                <Edit3 className="w-3.5 h-3.5" />
                <span>Edit</span>
              </button>
            </div>

            <div className="space-y-4 pt-3">
              {/* Target round */}
              <div className="flex items-center justify-between text-xs py-1">
                <span className="text-[#8E8E99] font-medium">Target round</span>
                <span className="text-base font-bold text-[#F2F2F4]">
                  {fundraisingNeeds.targetRound}
                </span>
              </div>

              {/* Timeline */}
              <div className="flex items-center justify-between text-xs py-1">
                <span className="text-[#8E8E99] font-medium">Timeline</span>
                <span className="text-base font-bold text-[#F2F2F4]">
                  {fundraisingNeeds.timeline}
                </span>
              </div>

              {/* Min. cheque */}
              <div className="flex items-center justify-between text-xs py-1">
                <span className="text-[#8E8E99] font-medium">Min. cheque</span>
                <span className="text-base font-bold text-[#F2F2F4]">
                  {fundraisingNeeds.minimumCheque}
                </span>
              </div>
            </div>
          </div>

          <div className="text-[11px] text-[#6E6E78] pt-2 border-t border-[#2C2C34]/60">
            Terms are shared with matched syndicates & angel networks.
          </div>
        </div>

        {/* [5] FOLLOW-UPS */}
        <div className="bg-[#131318] border border-[#2C2C34] rounded-2xl p-5 sm:p-6 space-y-4 flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between pb-2 border-b border-[#2C2C34]/60">
              <h2 className="text-sm font-bold text-[#F2F2F4]">Follow-ups</h2>
              <span className="text-[11px] text-[#8E8E99]">
                {followUps.length} pending
              </span>
            </div>

            <div className="space-y-3 pt-3">
              {followUps.map((fu) => {
                const isDue = fu.status === 'Due';
                const isBooked = fu.status === 'Booked';

                return (
                  <div
                    key={fu.id}
                    className="p-3.5 rounded-xl bg-[#08080A] border border-[#2C2C34] flex items-center justify-between gap-3 hover:border-[#3E3E48] transition-colors"
                  >
                    <div>
                      <div className="text-xs font-bold text-[#F2F2F4]">
                        {fu.title}
                      </div>
                      <div className="text-[11px] text-[#8E8E99] mt-0.5">
                        {fu.investor}
                      </div>
                    </div>

                    {/* Badge */}
                    <div>
                      {isDue && (
                        <span className="px-3.5 py-1 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-400 text-xs font-semibold">
                          Due
                        </span>
                      )}
                      {isBooked && (
                        <span className="px-3.5 py-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-400 text-xs font-semibold">
                          Booked
                        </span>
                      )}
                      {!isDue && !isBooked && (
                        <span className="px-3.5 py-1 rounded-full border border-sky-500/40 bg-sky-500/10 text-sky-400 text-xs font-semibold">
                          Open
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="text-[11px] text-[#6E6E78] pt-2 border-t border-[#2C2C34]/60">
            Action items synchronize automatically with Calendar deadlines.
          </div>
        </div>
      </div>

      {/* ========================================================
          ROW 4: [6] Investor interactions (Full width bottom card)
         ======================================================== */}
      <div className="bg-[#131318] border border-[#2C2C34] rounded-2xl p-5 sm:p-6 space-y-4">
        <div className="flex items-center justify-between pb-2 border-b border-[#2C2C34]/60">
          <h2 className="text-sm font-bold text-[#F2F2F4]">
            Investor interactions
          </h2>
          <button
            onClick={() => setLogModalOpen(true)}
            className="text-xs text-[#FF8EC5] hover:text-white font-medium flex items-center gap-1 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Log interaction</span>
          </button>
        </div>

        <div className="space-y-3">
          {interactions.map((it) => {
            const isFollowUp = it.status === 'Follow up';
            const isWaiting = it.status === 'Waiting';
            const isDone = it.status === 'Done';

            return (
              <div
                key={it.id}
                className="p-3.5 rounded-xl bg-[#08080A] border border-[#2C2C34] flex items-center justify-between gap-3 hover:border-[#3E3E48] transition-colors"
              >
                <div>
                  <div className="text-xs font-bold text-[#F2F2F4]">
                    {it.investorName}
                  </div>
                  <div className="text-[11px] text-[#8E8E99] mt-0.5">
                    {it.details}
                  </div>
                </div>

                {/* Right Badge */}
                <div className="shrink-0">
                  {isFollowUp && (
                    <span className="px-3.5 py-1 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-400 text-xs font-semibold">
                      Follow up
                    </span>
                  )}
                  {isWaiting && (
                    <span className="px-3.5 py-1 rounded-full border border-sky-500/40 bg-sky-500/10 text-sky-400 text-xs font-semibold">
                      Waiting
                    </span>
                  )}
                  {isDone && (
                    <span className="px-3.5 py-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-400 text-xs font-semibold">
                      Done
                    </span>
                  )}
                  {!isFollowUp && !isWaiting && !isDone && (
                    <span className="px-3.5 py-1 rounded-full border border-[#2C2C34] bg-[#18181F] text-[#8E8E99] text-xs font-semibold">
                      {it.status}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ========================================================
          MODAL 1: Edit Fundraising Needs
         ======================================================== */}
      {editTermsOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-150">
          <div className="w-full max-w-md rounded-2xl border border-[#2C2C34] bg-[#131318] p-6 shadow-2xl space-y-4 text-[#F2F2F4]">
            <div className="flex items-center justify-between border-b border-[#2C2C34] pb-3">
              <h3 className="text-base font-bold">Edit Fundraising Needs</h3>
              <button
                onClick={() => setEditTermsOpen(false)}
                className="p-1 rounded-lg text-[#8E8E99] hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveTerms} className="space-y-3.5 text-xs">
              <div>
                <label className="text-[#8E8E99] font-medium block mb-1">
                  Target Round (e.g. ₹1.5 Cr) *
                </label>
                <input
                  type="text"
                  value={termsForm.targetAmount}
                  onChange={(e) =>
                    setTermsForm({ ...termsForm, targetAmount: e.target.value })
                  }
                  className="w-full px-3.5 py-2.5 rounded-xl border border-[#2C2C34] bg-[#08080A] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
                  required
                />
              </div>

              <div>
                <label className="text-[#8E8E99] font-medium block mb-1">
                  Timeline (e.g. Q1 2027) *
                </label>
                <input
                  type="text"
                  value={termsForm.timeline}
                  onChange={(e) =>
                    setTermsForm({ ...termsForm, timeline: e.target.value })
                  }
                  className="w-full px-3.5 py-2.5 rounded-xl border border-[#2C2C34] bg-[#08080A] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
                  required
                />
              </div>

              <div>
                <label className="text-[#8E8E99] font-medium block mb-1">
                  Minimum Cheque Size (e.g. ₹10L) *
                </label>
                <input
                  type="text"
                  value={termsForm.minimumCheque}
                  onChange={(e) =>
                    setTermsForm({ ...termsForm, minimumCheque: e.target.value })
                  }
                  className="w-full px-3.5 py-2.5 rounded-xl border border-[#2C2C34] bg-[#08080A] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
                  required
                />
              </div>

              <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-[#2C2C34]">
                <button
                  type="button"
                  onClick={() => setEditTermsOpen(false)}
                  className="px-4 py-2 rounded-xl border border-[#2C2C34] text-xs text-[#8E8E99] hover:text-white"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={termsSaving}
                  className="px-5 py-2 rounded-xl bg-gradient-to-r from-[#D42B92] to-[#B02279] text-white font-semibold text-xs transition-all shadow-md shadow-[#D42B92]/30 disabled:opacity-50"
                >
                  {termsSaving ? 'Saving...' : 'Save Terms'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================
          MODAL 2: Log Investor Interaction
         ======================================================== */}
      {logModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-150">
          <div className="w-full max-w-md rounded-2xl border border-[#2C2C34] bg-[#131318] p-6 shadow-2xl space-y-4 text-[#F2F2F4]">
            <div className="flex items-center justify-between border-b border-[#2C2C34] pb-3">
              <h3 className="text-base font-bold">Log Investor Interaction</h3>
              <button
                onClick={() => setLogModalOpen(false)}
                className="p-1 rounded-lg text-[#8E8E99] hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveInteraction} className="space-y-3.5 text-xs">
              <div>
                <label className="text-[#8E8E99] font-medium block mb-1">
                  Investor / Firm Name *
                </label>
                <input
                  type="text"
                  value={interactionForm.investorName}
                  onChange={(e) =>
                    setInteractionForm({
                      ...interactionForm,
                      investorName: e.target.value,
                    })
                  }
                  className="w-full px-3.5 py-2.5 rounded-xl border border-[#2C2C34] bg-[#08080A] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
                  placeholder="e.g. Elevation Capital"
                  required
                />
              </div>

              <div>
                <label className="text-[#8E8E99] font-medium block mb-1">
                  Entity Type
                </label>
                <select
                  value={interactionForm.firmType}
                  onChange={(e) =>
                    setInteractionForm({
                      ...interactionForm,
                      firmType: e.target.value,
                    })
                  }
                  className="w-full px-3.5 py-2.5 rounded-xl border border-[#2C2C34] bg-[#08080A] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
                >
                  <option value="VC fund">VC fund</option>
                  <option value="angel network">angel network</option>
                  <option value="family office">family office</option>
                  <option value="Corporate VC">Corporate VC</option>
                </select>
              </div>

              <div>
                <label className="text-[#8E8E99] font-medium block mb-1">
                  Interaction Notes / Status Details *
                </label>
                <input
                  type="text"
                  value={interactionForm.notes}
                  onChange={(e) =>
                    setInteractionForm({ ...interactionForm, notes: e.target.value })
                  }
                  className="w-full px-3.5 py-2.5 rounded-xl border border-[#2C2C34] bg-[#08080A] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
                  placeholder="e.g. Intro call 24 Oct · Next: send financials"
                  required
                />
              </div>

              <div>
                <label className="text-[#8E8E99] font-medium block mb-1">
                  Pipeline Status
                </label>
                <select
                  value={interactionForm.status}
                  onChange={(e) =>
                    setInteractionForm({
                      ...interactionForm,
                      status: e.target.value,
                    })
                  }
                  className="w-full px-3.5 py-2.5 rounded-xl border border-[#2C2C34] bg-[#08080A] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
                >
                  <option value="Follow up">Follow up (Amber)</option>
                  <option value="Waiting">Waiting (Blue)</option>
                  <option value="Done">Done (Green)</option>
                </select>
              </div>

              <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-[#2C2C34]">
                <button
                  type="button"
                  onClick={() => setLogModalOpen(false)}
                  className="px-4 py-2 rounded-xl border border-[#2C2C34] text-xs text-[#8E8E99] hover:text-white"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={interactionSaving}
                  className="px-5 py-2 rounded-xl bg-gradient-to-r from-[#D42B92] to-[#B02279] text-white font-semibold text-xs transition-all shadow-md shadow-[#D42B92]/30 disabled:opacity-50"
                >
                  {interactionSaving ? 'Saving...' : 'Record Interaction'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
